import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { playbackActions } from 'store/playbackSlice/playbackSlice';

const usePlaybackErrorHandling = (videoRef, url) => {
    const dispatch = useDispatch();
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [videoError, setVideoError] = useState(null);

    useEffect(() => {
        setIsVideoLoading(true);
        setVideoError(null);
    }, [url]);

    const handlePlay = useCallback(() => {
        dispatch(playbackActions.setVideoPlaying(true));
    }, [dispatch]);

    const handlePause = useCallback(() => {
        dispatch(playbackActions.setVideoPlaying(false));
    }, [dispatch]);

    const handleCanPlay = useCallback(() => {
        setIsVideoLoading(false);
    }, []);

    const handleLoadedData = useCallback(() => {
        setIsVideoLoading(false);
    }, []);

    const handleWaiting = useCallback(() => {
        setIsVideoLoading(true);
    }, []);

    const handlePlaying = useCallback(() => {
        setIsVideoLoading(false);
    }, []);

    const handleError = useCallback(() => {
        setIsVideoLoading(false);
        const video = videoRef.current;
        const error = video?.error;
        let errorMessage = 'Failed to load video';

        if (error) {
            switch (error.code) {
                case MediaError.MEDIA_ERR_ABORTED:
                    errorMessage = 'Video loading was aborted';
                    break;
                case MediaError.MEDIA_ERR_NETWORK:
                    errorMessage = 'Network error - video could not be loaded';
                    break;
                case MediaError.MEDIA_ERR_DECODE:
                    errorMessage = 'Video decoding error';
                    break;
                case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
                    errorMessage = 'Video not found or format not supported';
                    break;
                default:
                    errorMessage = 'An error occurred while loading the video';
            }
        }
        setVideoError(errorMessage);
    }, [videoRef]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('playing', handlePlaying);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('loadeddata', handleLoadedData);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('error', handleError);

        if (!video.paused) {
            dispatch(playbackActions.setVideoPlaying(true));
        }

        return () => {
            video.removeEventListener('play', handlePlay);
            video.removeEventListener('pause', handlePause);
            video.removeEventListener('playing', handlePlaying);
            video.removeEventListener('canplay', handleCanPlay);
            video.removeEventListener('loadeddata', handleLoadedData);
            video.removeEventListener('waiting', handleWaiting);
            video.removeEventListener('error', handleError);
        };
    }, [videoRef, url, dispatch, handlePlay, handlePause, handlePlaying, handleCanPlay, handleLoadedData, handleWaiting, handleError]);

    return {
        isVideoLoading,
        videoError,
    };
};

export default usePlaybackErrorHandling;
